// src/pages/license/LicensesPage.tsx
// License hub — this platform's own compliance status up top, then the
// license registry below (one row on a local instance, many on a master).
// Detail is a right rail, not a page.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useLicense, useI18n } from '@react/hooks';
import { Button, Badge, Alert } from '@react/components';
import { Select } from '@/components/Select';
import type { License, LicenseStatus, LicenseStatusResponse } from '@core/types/license';
import { LicenseDetailRail } from '@/features/license/LicenseDetailRail';
import { IssueLicenseDialog } from '@/features/license/IssueLicenseDialog';
import { ActivateLicenseDialog } from '@/features/license/ActivateLicenseDialog';
import { LicenseStatusBadge, ViolationSeverityBadge } from '@/features/license/badges';
import { daysUntil, errMessage, fmtDate, listParams } from '@/features/license/format';

const STATUS_FILTERS: (LicenseStatus | '')[] = ['', 'LICENSE_STATUS_ACTIVE', 'LICENSE_STATUS_EXPIRED', 'LICENSE_STATUS_GRACE_PERIOD', 'LICENSE_STATUS_SUSPENDED', 'LICENSE_STATUS_REVOKED', 'LICENSE_STATUS_DRAFT'];

export const LicensesPage = observer(() => {
  const { t, locale } = useI18n();
  const store = useLicense();

  const [status, setStatus] = useState<LicenseStatus | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [platformStatus, setPlatformStatus] = useState<LicenseStatusResponse | null>(null);
  const [selected, setSelected] = useState<License | null>(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    setError(null);
    store.ListLicenses(listParams({ limit: 100 }, { status: status || undefined })).catch((e: unknown) => setError(errMessage(e, t('license.list.loadError'))));
  };

  const loadStatus = () => {
    store.GetLicenseStatus().then(setPlatformStatus).catch(() => setPlatformStatus(null));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sync = async () => {
    setSyncing(true);
    setError(null);
    try {
      await store.SyncLicense({ entity: undefined });
      loadStatus();
      load();
    } catch (e) {
      setError(errMessage(e, t('license.list.syncError')));
    } finally {
      setSyncing(false);
    }
  };

  const openRow = async (row: License) => {
    let full = row;
    if (row.entity?.id) {
      try { full = await store.GetLicense(row.entity.id); } catch { full = row; }
    }
    setSelected(full);
  };

  const remaining = platformStatus?.license ? daysUntil(platformStatus.license.valid_until) : null;

  return (
    <div className="flex h-full">
      <div className="min-w-0 flex-1 space-y-4 overflow-y-auto p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-semibold text-foreground">{t('license.list.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('license.list.subtitle')}</p>
          </div>
          <div className="flex gap-2">
            <Link to="/license/settings" className="text-sm text-primary hover:underline self-center">{t('license.settings.title')}</Link>
            <Button size="sm" variant="secondary" onClick={sync} isLoading={syncing}>{t('license.list.sync')}</Button>
            <Button size="sm" variant="secondary" onClick={() => setActivateOpen(true)}>{t('license.activate.title')}</Button>
            <Button size="sm" onClick={() => setIssueOpen(true)}>{t('license.issue.title')}</Button>
          </div>
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        {platformStatus?.license && (
          <div className="rounded-lg border border-border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-foreground">{platformStatus.license.name}</h2>
                <LicenseStatusBadge status={platformStatus.license.status} />
                <Badge variant={platformStatus.is_compliant ? 'success' : 'error'}>
                  {platformStatus.is_compliant ? t('license.detail.compliant') : t('license.list.nonCompliant')}
                </Badge>
              </div>
              {remaining !== null && (
                <Badge variant={remaining < 0 ? 'error' : remaining < 14 ? 'warning' : 'default'}>
                  {remaining >= 0 ? t('license.list.daysRemaining', { count: remaining }) : t('license.list.expiredDaysAgo', { count: Math.abs(remaining) })}
                </Badge>
              )}
            </div>
            {platformStatus.violations.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {platformStatus.violations.map((v, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    {v.violation_type} <ViolationSeverityBadge severity={v.severity} />
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {!platformStatus?.license && (
          <Alert variant="info">
            {t('license.list.noOwnLicense')}
          </Alert>
        )}

        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as LicenseStatus | '')}
          className="w-56"
          options={STATUS_FILTERS.map((s) => ({ value: s, label: s ? t(`license.status.${s.replace('LICENSE_STATUS_', '').toLowerCase()}`) : t('common.all') }))}
        />

        {store.isLoading && store.items.length === 0 && <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy="true" />}
        {!store.isLoading && store.items.length === 0 && !error && (
          <div className="rounded-md border border-dashed border-border p-8 text-center">
            <p className="text-sm text-muted-foreground">{t('license.list.empty')}</p>
          </div>
        )}

        <div className="space-y-2">
          {store.items.map((l) => (
            <button
              key={l.entity?.id}
              onClick={() => openRow(l)}
              className={`w-full rounded-md border p-3 text-left hover:bg-muted ${selected?.entity?.id === l.entity?.id ? 'border-primary' : 'border-border'}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-foreground">{l.name}</span>
                <LicenseStatusBadge status={l.status} />
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('license.field.validUntil')}: {fmtDate(l.valid_until, locale)}
              </p>
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <LicenseDetailRail
          license={selected}
          onClose={() => setSelected(null)}
          onChanged={(updated) => { setSelected(updated); load(); loadStatus(); }}
          onRemoved={() => { setSelected(null); load(); loadStatus(); }}
        />
      )}

      <IssueLicenseDialog open={issueOpen} onClose={() => setIssueOpen(false)} onSaved={() => load()} />
      <ActivateLicenseDialog open={activateOpen} onClose={() => setActivateOpen(false)} onSaved={() => { load(); loadStatus(); }} />
    </div>
  );
});
